import { RefObject, useLayoutEffect, useState } from "react";

// Layout knobs (safe to tune). Card width is derived so `full` cards + PEEK_FRACTION of the
// next one exactly fill the scroller, leaving a partial card peeking as a "more plans →" hint.
const CARD_GAP = 24;
const MIN_CARD_WIDTH = 210;
const PEEK_FRACTION = 0.4;

// Annual cards carry longer content, so show one fewer full card on the yearly tab.
function targetFullCards(width: number, isYearly: boolean): number {
  const base =
    width >= 1180 ? 5
    : width >= 960 ? 4
    : width >= 720 ? 3
    : width >= 500 ? 2
    : 1;
  return isYearly ? Math.max(1, base - 1) : base;
}

// Derives card width from the scroller's real width so `full` cards + a peek fit at any
// resolution. Re-measures on resize, when `isYearly` changes, and once `ready` (the scroller
// mounts). Returns the shared gap too, so all layout knobs live in one place.
export function useResponsiveCardWidth(
  scrollerRef: RefObject<HTMLElement>,
  isYearly: boolean,
  ready: boolean,
) {
  const [cardWidth, setCardWidth] = useState(MIN_CARD_WIDTH);

  useLayoutEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const measure = () => {
      const width = el.clientWidth;
      if (!width) return;
      const full = targetFullCards(width, isYearly);
      const derived = (width - full * CARD_GAP) / (full + PEEK_FRACTION);
      setCardWidth(Math.max(MIN_CARD_WIDTH, derived));
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [ scrollerRef, isYearly, ready ]);

  return { cardWidth, cardGap: CARD_GAP };
}
